/**
 * Shared multilingual auto-translate helper (Claude Sonnet 4.5 via
 * /api/admin/translate). Used by both the per-field LocalizedField button
 * and the form-level "Translate ALL" master button so the logic stays DRY.
 */
const API = process.env.REACT_APP_BACKEND_URL;

export const TARGETS = ['nl', 'ro', 'ru'];
export const LANG_LABEL = { en: 'EN', nl: 'NL', ro: 'RO', ru: 'RU' };

/** Languages (NL/RO/RU) that are still empty for a given LocalizedString. */
export function getMissingTargets(value) {
  const v = value || {};
  return TARGETS.filter((l) => !((v[l] || '').trim()));
}

/** True when there is an English source AND at least one empty target. */
export function canTranslate(value) {
  const v = value || {};
  return Boolean((v.en || '').trim()) && getMissingTargets(v).length > 0;
}

/**
 * Translate the English source of a single LocalizedString into its empty
 * target languages. Existing translations are preserved — only blank tabs
 * are filled. Returns { value, filled, targets }.
 */
export async function translateLocalizedValue(token, value) {
  const v = value || { en: '', nl: '', ro: '', ru: '' };
  const sourceEn = (v.en || '').trim();
  const missing = getMissingTargets(v);
  if (!sourceEn || missing.length === 0) {
    return { value: v, filled: 0, targets: [] };
  }
  const res = await fetch(`${API}/api/admin/translate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ text: sourceEn, targets: missing }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Auto-translate failed');
  }
  const data = await res.json();
  const next = { ...v };
  const filledLangs = [];
  missing.forEach((lang) => {
    const t = (data.translations?.[lang] || '').trim();
    if (t) { next[lang] = t; filledLangs.push(lang); }
  });
  return { value: next, filled: filledLangs.length, targets: filledLangs };
}
